'use client'

/**
 * VersionGate — the force-update surface, driven by {@link useVersionCheck}.
 *
 * Renders nothing in the common case. When the platform signals an update:
 *  - **Native too old**: a non-dismissible full-screen gate (brand gradient,
 *    matching SplashScreen) with an "update now" button → store. Apple gives us
 *    no real force mechanism, so this block-until-update screen IS the force.
 *  - **Web build stale**: a brief notice, then a hard reload (per product
 *    choice, web always auto-reloads). The reload goes through hard-nav so the
 *    native PowerSync connection is released first (web is a passthrough).
 *
 * Mounted high in AppRoot so it overlays everything — including login — because
 * a forced update must apply regardless of where the user is.
 */
import { useEffect, useState } from 'react'
import { useTranslations } from '@/hooks/useTranslations'
import { hardReload } from '@/lib/hard-nav'
import { useVersionCheck } from '@/hooks/useVersionCheck'

// Grace before the web auto-reload, so a new build doesn't yank the page out
// from under a click. Short — the bundle is already stale, we just soften it.
const WEB_RELOAD_GRACE_SECS = 5

function Backdrop({ children, testId }: { children: React.ReactNode; testId: string }) {
  return (
    <div
      role="alertdialog"
      aria-modal="true"
      data-testid={testId}
      className="fixed inset-0 z-[1000] flex flex-col items-center justify-center overflow-hidden touch-none select-none px-8"
      style={{ height: '100dvh' }}
    >
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(to top right, #011c49 0%, #0c324f 30%, #124a58 55%, #1f7165 100%)',
        }}
      />
      <div className="relative z-10 flex flex-col items-center text-center max-w-sm">
        <img
          src="/icon-512.png"
          alt="ZeroSnag"
          className="w-28 h-28 rounded-[20%] mb-8"
          style={{ boxShadow: '0 20px 45px rgba(0, 0, 0, 0.5)' }}
        />
        {children}
      </div>
    </div>
  )
}

export function VersionGate() {
  const { nativeUpdateRequired, webReloadPending, openStore } = useVersionCheck()
  const t = useTranslations('appUpdate')
  const [secs, setSecs] = useState(WEB_RELOAD_GRACE_SECS)

  useEffect(() => {
    if (!webReloadPending) return
    if (secs <= 0) {
      void hardReload()
      return
    }
    const id = setTimeout(() => setSecs((s) => s - 1), 1000)
    return () => clearTimeout(id)
  }, [webReloadPending, secs])

  if (nativeUpdateRequired) {
    return (
      <Backdrop testId="version-update-gate">
        <h1 className="text-white text-2xl font-bold mb-3 drop-shadow">
          {t('updateRequiredTitle')}
        </h1>
        <p className="text-white/85 text-base mb-8 drop-shadow">{t('updateRequiredBody')}</p>
        <button
          type="button"
          onClick={openStore}
          className="px-8 py-3 rounded-full bg-white text-[#011c49] font-semibold shadow-lg active:scale-[0.98] transition-transform"
        >
          {t('updateButton')}
        </button>
      </Backdrop>
    )
  }

  if (webReloadPending) {
    return (
      <Backdrop testId="version-reload-overlay">
        <h1 className="text-white text-2xl font-bold mb-3 drop-shadow">
          {t('newVersionTitle')}
        </h1>
        <p className="text-white/85 text-base drop-shadow">
          {t('reloadingIn', { secs: Math.max(0, secs) })}
        </p>
      </Backdrop>
    )
  }

  return null
}
